import React, { useRef, useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import InputWithChecks from "../../shared/InputWithChecks";
import Container from "../../shared/Container";
import PrimaryButton from "../../shared/PrimaryButton";
import BoxBorder from "../../shared/BoxBorder";
import LoadingIndicatorButton from "../../shared/LoadingIndicatorButton";
import InfoIcon from "../../shared/InfoIcon";
import {
   validateRequired,
   validateForm,
   validateMinLength,
   validatePositiveNumber,
} from "../../../util/shared/validation";
import { addProduct, updateProduct } from "../../../util/admin/httpAdmin";
import { queryClient } from "../../../util/admin/httpAdmin";
import CollectionDropdown from "./CollectionDropdown";
import SuccessMessage from "../../shared/SuccessMessage";
import styles from "./ProductFormAdmin.module.css";
import deleteIcon from "../../../assets/admin/icons/delete-white.svg";
import ProductOptionsEditor from "../shared/ProductOptionsEditor";

const S3_BUCKET_NAME = "resto-online";
const S3_BUCKET_REGION = "eu-north-1";
const S3_BASE_URL = `https://${S3_BUCKET_NAME}.s3.${S3_BUCKET_REGION}.amazonaws.com/`;
const PLACEHOLDER_IMAGE = "https://via.placeholder.com/300";

const ProductFormAdmin = ({
   onSuccessClose,
   editMode = false,
   previousData = {},
}) => {
   const nameRef = useRef(null);
   const descriptionRef = useRef(null);
   const priceRef = useRef(null);
   const collectionRef = useRef(null);
   const stockQuantityRef = useRef(null);
   const soldAloneRef = useRef(null);

   const [imageFile, setImageFile] = useState(null);
   const [imagePreview, setImagePreview] = useState(
      editMode && previousData.hasImage
         ? `${S3_BASE_URL}${previousData._id}?t=${Date.now()}`
         : PLACEHOLDER_IMAGE
   );
   const [successMessage, setSuccessMessage] = useState("");
   const closeTimeoutRef = useRef(null);
   const messageTimeoutRef = useRef(null);
   const [productOptions, setProductOptions] = useState(
      editMode && previousData.productOptions 
         ? previousData.productOptions.map(option => ({
            ...option,
            options: option.options.map(choice => ({
               ...choice,
               price: choice.price?.$numberDecimal || choice.price || 0
            })),
            maxSelections: option.maxSelections || 1
         }))
         : []
   );
   const optionNameRefs = useRef([]);
   const choiceNameRefs = useRef([]);
   const choicePriceRefs = useRef([]);
   const maxSelectionsRefs = useRef([]);

   useEffect(() => {
      // Cleanup function
      return () => {
         if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
         }
         if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
         }
      };
   }, []);

   // Reset de imageFile wanneer er een ander product wordt bewerkt of wanneer
   // we switchen tussen toevoegen en bewerken
   useEffect(() => {
      // Reset de afbeeldingsstate wanneer het bewerkte product verandert
      setImageFile(null);
      setImagePreview(
         editMode && previousData.hasImage
            ? `${S3_BASE_URL}${previousData._id}?t=${Date.now()}`
            : PLACEHOLDER_IMAGE
      );
      // console.log("Reset image state for product:", previousData._id || "new product");
   }, [editMode, previousData._id, previousData.hasImage]);

   useEffect(() => {
      // Initialize refs arrays
      optionNameRefs.current = optionNameRefs.current.slice(0, productOptions.length);
      choiceNameRefs.current = choiceNameRefs.current.slice(0, productOptions.length);
      choicePriceRefs.current = choicePriceRefs.current.slice(0, productOptions.length);
   }, [productOptions]);

   useEffect(() => {
      if (editMode && previousData) {
         try {
            const price = previousData.price?.$numberDecimal || previousData.price;
            
            setProductOptions(previousData.productOptions?.map(option => ({
               ...option,
               options: option.options.map(choice => ({
                  ...choice,
                  price: choice.price?.$numberDecimal || choice.price || 0
               })),
               maxSelections: option.maxSelections || 1
            })) || []);
            
            setImagePreview(previousData.hasImage ? `${S3_BASE_URL}${previousData._id}?t=${Date.now()}` : PLACEHOLDER_IMAGE);
            
            // Gebruik de veilige manier om setValue te gebruiken
            const setRefValue = (ref, value) => {
               if (ref && ref.current && typeof ref.current.setValue === 'function') {
                  ref.current.setValue(value || "");
               } else {
                  console.log(`setValue functie bestaat niet op de ref`);
               }
            };
            
            setRefValue(nameRef, previousData.name);
            setRefValue(descriptionRef, previousData.description);
            setRefValue(priceRef, price);
            setRefValue(collectionRef, previousData.collection ? previousData.collection._id : "");
            setRefValue(stockQuantityRef, previousData.stockQuantity);
            setRefValue(soldAloneRef, previousData.soldAlone === undefined ? true : previousData.soldAlone ? "true" : "false");
         } catch (error) {
            console.error('Error setting refs:', error);
         }
      }
   }, [editMode, previousData]);

   const mutationFn = editMode ? updateProduct : addProduct;

   const { mutate, isPending, isError, error } = useMutation({
      mutationFn,
      onSuccess: () => {
         setSuccessMessage(
            editMode
               ? "Product succesvol bijgewerkt!"
               : "Product succesvol toegevoegd!"
         );
         queryClient.invalidateQueries(["adminProducts"]);
         
         closeTimeoutRef.current = setTimeout(() => {
            if (onSuccessClose) {
               onSuccessClose();
            }
            messageTimeoutRef.current = setTimeout(() => {
               setSuccessMessage("");
            }, 100);
         }, 500);
      },
      onError: (error) => {
         console.error('Mutation error:', error);
      },
   });

   const handleImageChange = (event) => {
      // Als er een bestand is geselecteerd
      if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
         console.log('New image selected for product:', {
            productId: editMode ? previousData._id : 'new product',
            fileName: file.name,
            type: file.type,
            size: file.size,
            lastModified: new Date(file.lastModified).toISOString()
         });
         
         // Check file size (max 10MB)
         const MAX_SIZE = 40 * 1024 * 1024; // 10MB
         if (file.size > MAX_SIZE) {
            console.error('File too large:', file.size);
            alert('De afbeelding is te groot. Maximum toegestane grootte is 10MB.');
            return;
         }
         
         // Validate file type more thoroughly
         const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff'];
         
         if (!file.type.startsWith('image/')) {
            console.error('Invalid file type:', file.type);
            alert('Alleen afbeeldingen zijn toegestaan.');
            return;
         }
         
         // For certain problematic formats, show a warning
         if (!validImageTypes.includes(file.type)) {
            console.warn('Unusual image format:', file.type);
            alert('Let op: Dit is een ongebruikelijk afbeeldingsformaat. Als er problemen zijn bij het uploaden, probeer dan een JPEG of PNG bestand.');
         }
         
         // Create a new image to validate and get dimensions
         try {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);
            
            img.onload = () => {
               console.log('Image loaded successfully:', {
                  width: img.width,
                  height: img.height
               });
               
               // Check if dimensions are too large
               if (img.width > 4000 || img.height > 4000) {
                  console.warn('Very large image dimensions:', img.width, 'x', img.height);
                  alert('Deze afbeelding heeft erg grote afmetingen. Dit kan problemen veroorzaken bij het uploaden. Overweeg om de afbeelding te verkleinen.');
               }
               
               URL.revokeObjectURL(objectUrl);
               
               // Accept the image
               setImageFile(file);
               setImagePreview(URL.createObjectURL(file));
            };
            
            img.onerror = () => {
               console.error('Failed to load image');
               URL.revokeObjectURL(objectUrl);
               alert('De geselecteerde afbeelding kan niet worden geladen. Probeer een andere afbeelding of een ander bestandsformaat.');
            };
            
            img.src = objectUrl;
         } catch (error) {
            console.error('Error processing image:', error);
            alert('Er is een fout opgetreden bij het verwerken van de afbeelding: ' + error.message);
         }
      } else {
         // Als er geen bestand is geselecteerd (cancel knop is geklikt)
         console.log('No image selected or selection canceled');
      }
   };

   const compressImage = async (file) => {
      return new Promise((resolve, reject) => {
         try {
            console.log('Original image size:', file.size);
            
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            img.onload = () => {
               try {
                  // Calculate new dimensions (max 800px width/height while maintaining aspect ratio)
                  const MAX_SIZE = 800;
                  let width = img.width;
                  let height = img.height;
                  
                  if (width > height && width > MAX_SIZE) {
                     height = Math.round(height * (MAX_SIZE / width));
                     width = MAX_SIZE;
                  } else if (height > MAX_SIZE) {
                     width = Math.round(width * (MAX_SIZE / height));
                     height = MAX_SIZE;
                  }
                  
                  // Set canvas dimensions
                  canvas.width = width;
                  canvas.height = height;
                  
                  // Draw image on canvas with new dimensions
                  ctx.drawImage(img, 0, 0, width, height);
                  
                  // Convert to compressed JPEG blob with 80% quality
                  canvas.toBlob(
                     (blob) => {
                        if (blob) {
                           console.log('Compressed image size:', blob.size);
                           
                           // Create a new File object from the blob
                           const compressedFile = new File(
                              [blob],
                              'compressed-' + file.name.replace(/\.[^/.]+$/, ".jpg"),
                              { type: 'image/jpeg' }
                           );
                           
                           resolve(compressedFile);
                        } else {
                           reject(new Error('Failed to compress image'));
                        }
                     },
                     'image/jpeg',
                     0.8
                  );
               } catch (error) {
                  reject(error);
               }
            };
            
            img.onerror = () => reject(new Error('Failed to load image for compression'));
            
            // Load the image from the file
            img.src = URL.createObjectURL(file);
         } catch (error) {
            reject(error);
         }
      });
   };

   const handleFormSubmit = async (e) => {
      e.preventDefault();

      const nameValid = nameRef.current?.validateInput();
      const priceValid = priceRef.current?.validateInput();
      const soldAloneValid = soldAloneRef.current?.validateInput();

      if (nameValid && priceValid && soldAloneValid) {
         try {
            // Update product options with current values from refs
            const updatedOptions = productOptions.map((option, optionIndex) => {
               const updatedOption = { ...option };
               updatedOption.name = optionNameRefs.current[optionIndex]?.getValue() || "";
               
               if (option.type === "select" || option.type === "multiselect") {
                  updatedOption.options = option.options.map((choice, choiceIndex) => ({
                     name: choiceNameRefs.current[optionIndex]?.[choiceIndex]?.getValue() || "",
                     price: parseFloat(choicePriceRefs.current[optionIndex]?.[choiceIndex]?.getValue() || 0)
                  }));
                  
                  // If it's a multiselect, get maxSelections from the ref
                  if (option.type === "multiselect") {
                     const maxSelectionsRef = maxSelectionsRefs.current[optionIndex];
                     if (maxSelectionsRef && maxSelectionsRef.current) {
                        updatedOption.maxSelections = parseInt(maxSelectionsRef.current.value || "1");
                     } else {
                        updatedOption.maxSelections = option.maxSelections || 1;
                     }
                  }
               }
               
               return updatedOption;
            });

            const formData = new FormData();
            formData.append("name", nameRef.current.getValue());
            formData.append("description", descriptionRef.current.getValue());
            formData.append("price", parseFloat(priceRef.current.getValue()));
            
            const collectionValue = collectionRef.current.getValue();
            if (collectionValue) {
               formData.append("collection", collectionValue);
               formData.append("icon", "📦");
            }

            const stockQuantity = stockQuantityRef.current.getValue();
            if (stockQuantity && String(stockQuantity).trim() !== "") {
               formData.append("stockQuantity", parseInt(stockQuantity));
            }
            
            formData.append("soldAlone", soldAloneRef.current.getValue() === "true");
            formData.append("productOptions", JSON.stringify(updatedOptions));

            // If there's an image file, compress it before uploading
            if (imageFile) {
               console.log('Sending new image for product:', 
                  editMode ? previousData._id : 'new product');
               try {
                  const compressedFile = await compressImage(imageFile);
                  formData.append("image", compressedFile);
                  console.log('Added compressed image to FormData');
               } catch (error) {
                  console.error("Error compressing image:", error);
                  // Fallback to original image if compression fails
                  formData.append("image", imageFile);
                  console.log('Compression failed, added original image to FormData');
               }
            } else {
               console.log('No new image to upload for product:', 
                  editMode ? previousData._id : 'new product');
               // Add a flag to indicate that we want to keep the existing image
               if (editMode) {
                  formData.append("keepExistingImage", "true");
               }
            }

            if (editMode && previousData?._id) {
               formData.append("_id", previousData._id);
            }
            
            console.log('Form data prepared, sending to server');
            mutate(formData);
         } catch (error) {
            console.error("Error preparing form data:", error);
            alert("Er is een fout opgetreden bij het voorbereiden van het formulier.");
         }
      }
   };

   return (
      <section>
         <Container>
            {successMessage ? (
               <SuccessMessage title="Succes!" description={successMessage} />
            ) : (
               <BoxBorder extraClasses="mxw-1000 m-a g-20">
                  <form className="g-20">
                     <div className="g-1-1 a-i-s gap-1">
                        <InputWithChecks
                           ref={nameRef}
                           label="Productnaam"
                           inputType="text"
                           validate={(value) =>
                              validateForm(value, [
                                 validateRequired,
                                 (v) => validateMinLength(v, 2),
                              ])
                           }
                           placeholder="Voer de productnaam in"
                           value={editMode ? previousData.name.toString() : ""}
                        />
                        <CollectionDropdown
                           ref={collectionRef}
                           value={editMode && previousData.collection ? previousData.collection._id : ""}
                        />
                        <InputWithChecks
                           style={{height: "150px"}}
                           ref={descriptionRef}
                           label="Beschrijving"
                           inputType="textarea"
                           placeholder="Voer een productbeschrijving in"
                           value={editMode ? previousData.description : ""}
                           className="description-textarea"
                        />
                        {/* Nieuwe afbeelding upload sectie */}
                        <div className="g-10">
                           <label>Product Afbeelding</label>
                           <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                           />
                           <img
                              src={imagePreview}
                              alt="Product preview"
                              className="mxw-150"
                           />
                        </div>
                     </div>
                     <div className="g-1-1-1 a-i-s gap-1">
                        <InputWithChecks
                              ref={priceRef}
                              label="Prijs"
                              inputType="number"
                              validate={(value) =>
                                 validateForm(value, [
                                    validateRequired,
                                    validatePositiveNumber,
                                 ])
                              }
                              placeholder="Voer de productprijs in"
                              value={editMode ? (previousData.price?.$numberDecimal || previousData.price) : ""}
                           />
                        <InputWithChecks
                           ref={soldAloneRef}
                           label={
                              <>
                                 Apart verkopen
                                 <InfoIcon info="Kan dit product apart verkocht worden?" />
                              </>
                           }
                           inputType="select"
                           validate={validateRequired}
                           value={
                              editMode
                                 ? previousData.soldAlone
                                    ? "true"
                                    : "false"
                                 : ""
                           }
                        >
                           <option value="">Selecteer een optie</option>
                           <option value="true">Ja</option>
                           <option value="false">Nee</option>
                        </InputWithChecks>
                        <InputWithChecks
                           ref={stockQuantityRef}
                           label={
                              <>
                                 Voorraad
                                 <InfoIcon info="Hoeveel vooraad heb je van dit product?Niet verplicht; laat leeg voor ongelimiteerde voorraad." />
                              </>
                           }
                           inputType="number"
                           placeholder="Aantal producten in voorraad"
                           value={editMode ? previousData.stockQuantity : ""}
                        />
                     </div>
                        {/* Product Options Section */}
                        <ProductOptionsEditor 
                           productOptions={productOptions} 
                           setProductOptions={setProductOptions} 
                           refs={{
                              optionNameRefs,
                              choiceNameRefs,
                              choicePriceRefs,
                              maxSelectionsRefs
                           }}
                        />
                     {isError && (
                        <div className="c-r">
                           {error.info?.message ||
                              "Er is iets fout gegaan tijdens het verwerken van het product."}
                        </div>
                     )}
                     <div>
                        <PrimaryButton onClick={handleFormSubmit} disabled={isPending}>
                           <span className="flex-0-5">
                              {editMode ? "Product Bijwerken" : "Product Toevoegen"}
                              {isPending && <LoadingIndicatorButton />}
                           </span>
                        </PrimaryButton>
                     </div>
                  </form>
               </BoxBorder>
            )}
         </Container>
      </section>
   );
};

export default ProductFormAdmin;
